import React, {ChangeEventHandler, FocusEventHandler, useCallback, useEffect, useRef} from 'react';
import './TextArea.less';
import '../../stylesheets/print.less';
import ReactQuill, {Quill} from 'react-quill-new';
import ImageCompressModule from 'quill-image-compress';
const ImageCompress = (ImageCompressModule as any).default || ImageCompressModule;
import 'react-quill-new/dist/quill.snow.css';

// Define types for Quill formats
interface QuillFormat {
    whitelist?: string[];
    [key:string]: any;
}

interface QuillFontFormat extends QuillFormat {
    whitelist: string[];
}

interface QuillSizeFormat extends QuillFormat {
    whitelist: string[];
}

// Work with Quill's font and size
const Font = Quill.import('formats/font') as QuillFontFormat;
const Size = Quill.import('formats/size') as QuillSizeFormat;

/**
 * A good list of candidate fonts
 *
 * WARNING! If you modify this section, you will need to uncomment the console.log statement before the
 * whitelists and copy the logged output to print.less
 */
export const FONT_FAMILIES = [
    'Andale Mono, monospace',
    'Calibri, sans-serif',
    'Comic Sans MS, cursive, sans-serif',
    'Courier New, Courier, monospace',
    'Frederika the Great, cursive',
    'Nunito, sans-serif',
    'Georgia, serif',
    'Helvetica',
    'Impact, Charcoal, sans-serif',
    'Inconsolata, monospace',
    'Lucida Sans, Lucida Grande, sans-serif',
    'Lucida Console, Monaco, monospace',
    'Open Sans, sans-serif',
    'Palatino Linotype, sans-serif',
    'Poppins, sans-serif',
    'Spectral SC, serif',
    'Tahoma, Geneva, sans-serif',
    'Times New Roman, Times, serif',
    'Trebuchet MS, Helvetica, sans-serif',
    'Verdana, sans-serif'
];

export const FONT_SIZES = [8, 9, 10, 11, 12, 13, 14, 16, 18, 20, 22, 24, 28, 30].map(size => `${size}pt`);

/**
 * Get a lower-case version of the font name for reference.
 *
 * @param {string} entry
 * @return {string}
 */
const getFontId = (entry: string): string => {
    // Note: quilljs has a bug where font names with a dash are ignored. Must be ql-font-oneWord.
    return entry.split(',')[0].replace(/\s+/g, '').toLowerCase();
};

const quoteFonts = (entry: string) => {
    return entry.split(',').map(e => e.trim().includes(' ') ? `"${e.trim()}"` : e);
};

// We will be using Composable Style Sheets
const sheet = Array.from(document.styleSheets).find(ss => ss.title === 'printStyles');
const styleExport = [];

// For each font, create the requisite styles to make the text look good both in the dropdown AND the content.
FONT_FAMILIES.forEach(fontFamily => {
    const fontId = getFontId(fontFamily);
    const quoted = quoteFonts(fontFamily);
    const fontName = fontFamily.split(',')[0];
    sheet.insertRule(`.ql-font-${fontId}, .ql-font span[data-value="${fontId}"]::before { font-family: ${quoted}; }`);
    styleExport.push(`.ql-font-${fontId}, .ql-font span[data-value="${fontId}"]::before { font-family: ${quoted}; }`);
    styleExport.push(`.ql-picker-item[data-value="${fontId}"]::before { content: "${fontName}" !important; font-family: ${quoted}; }`)
    styleExport.push(`.ql-picker-label[data-value="${fontId}"]::before { content: "${fontName}" !important; font-family: ${quoted}; }`)
});

FONT_SIZES.forEach(size => {
    sheet.insertRule(`.ql-size-${size} { font-size: ${size} !important;}`);
    styleExport.push(`.ql-size-${size} { font-size: ${size} !important;}`);
    styleExport.push(`.ql-picker-item[data-value="${size}"]::before { content: "${size}" !important; font-size: ${size}; }`)
    styleExport.push(`.ql-picker-label[data-value="${size}"]::before { content: "${size}" !important; font-size: ${size}; }`)
});

export const STYLES = '<link href="https://cdn.jsdelivr.net/npm/quill@2.0.3/dist/quill.js" rel="stylesheet">';

export const STYLESHEET = `
    <link href="https://cdn.jsdelivr.net/npm/quill@2.0.3/dist/quill.js" rel="stylesheet">
    <link href="https://cdn.jsdelivr.net/npm/quill@2.0.3/dist/quill.snow.css" rel="stylesheet">
    <style>
        ${styleExport.join('\n')}
    </style>
`;

// Use this to get an updated style sheet
// console.log(styleExport.join('\n'));

// Set whitelists for fonts and FONT_SIZES
Font.whitelist = FONT_FAMILIES.map(fontFamily => getFontId(fontFamily));
Size.whitelist = FONT_SIZES;

// Register our fonts and FONT_SIZES
Quill.register(Font, true);
Quill.register(Size, true);
Quill.register('modules/imageCompress', ImageCompress);

const modules = {
    toolbar: [
        [{'font': Font.whitelist}],
        [{'size': Size.whitelist}],
        ['bold', 'italic', 'underline'],
        [{'align': []}],
        [
            {list: 'ordered'},
            {list: 'bullet'},
            {list: 'check'},
        ],
        [
            {indent: '-1'},
            {indent: '+1'},
        ],
        ['link', 'image', 'video']
    ],
    clipboard: {
        // toggle to add extra line breaks when pasting HTML:
        matchVisual: false,
        // allowed: {
        //     tags: ['a', 'b', 'strong', 'u', 's', 'i', 'p', 'br', 'ul', 'ol', 'li', 'span'],
        //     attributes: ['href', 'rel', 'target', 'class']
        // },
        // keepSelection: true,
        // substituteBlockElements: true,
        // magicPasteLinks: true
    },
    imageCompress: {
        quality: .9, // default
        maxWidth: 3000, // default
        maxHeight: 3000, // default
        imageType: 'image/jpeg', // default
        debug: false, // default
    }
};

const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "align",
    "indent",
    "link",
    "image",
    "video",
    "color"
];

function TextArea(props: TextAreaProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const quillRef = useRef<ReactQuill>(null);

    const hasChangedRef = useRef(false);
    const lastValueRef = useRef<string | null>(null);

    // True when the focus the editor is about to receive was initiated by a
    // mouse/touch press (a click into the content) rather than the keyboard
    // (Tab from the title). onRichFocus uses this to decide whether to park the
    // caret at the end — see there (OWA-592).
    const focusFromPointerRef = useRef(false);

    // Remove toolbar buttons from the tab order so Tab skips from the title
    // field directly into the Quill editor content area.
    useEffect(() => {
        if (!props.isRichText || !containerRef.current) return;
        const toolbar = containerRef.current.querySelector('.ql-toolbar');
        if (!toolbar) return;
        toolbar.querySelectorAll<HTMLElement>('button, select, .ql-picker-label, input')
            .forEach(el => el.setAttribute('tabindex', '-1'));
    }, [props.isRichText]);

    // Mark focus as pointer-initiated. A capture-phase listener runs before the
    // focus event fires, so onRichFocus can tell a click into the content from a
    // keyboard/Tab focus (OWA-592).
    useEffect(() => {
        const el = containerRef.current;
        if (!props.isRichText || !el) return;
        const markPointer = () => {focusFromPointerRef.current = true;};
        el.addEventListener('pointerdown', markPointer, true);
        return () => el.removeEventListener('pointerdown', markPointer, true);
    }, [props.isRichText]);

    const onBlur: FocusEventHandler<HTMLTextAreaElement> = useCallback((e) => {
        if (props.onBlur) props.onBlur(e.target.value, e.target.value);
    }, [props]);

    const onChange: ChangeEventHandler<HTMLTextAreaElement> = useCallback((e) => {
        if (props.onChange) props.onChange(e.target.value, e.target.value);
    }, [props]);

    const onRichBlur = useCallback((_previousRange: ReactQuill.Range, _sources: any, editor: ReactQuill.UnprivilegedEditor) => {
        // Clear the pointer flag on blur so a later click that doesn't change
        // focus can't leave it set for a subsequent keyboard focus.
        focusFromPointerRef.current = false;
        if (hasChangedRef.current && props.onBlur) props.onBlur(editor.getSemanticHTML(), editor.getText());
    }, [props]);

    const onRichFocus = useCallback(() => {
        setTimeout(() => {
            const editor = quillRef.current?.getEditor();
            if (!editor) return;

            // Keyboard/Tab focus from the title field parks the caret at the end
            // of the note so the user appends to it (commit 5c7049529). But a
            // MOUSE/TOUCH click into existing text must keep the caret at the
            // click point: forcing it to the end yanked the user's cursor to the
            // bottom of the note the instant they clicked in to edit, sending the
            // next character they typed to the end (OWA-592). Only jump to the
            // end for non-pointer (keyboard) focus.
            const fromPointer = focusFromPointerRef.current;
            focusFromPointerRef.current = false;
            if (fromPointer) return;

            const length = editor.getLength();
            if (length > 1) {
                editor.setSelection(length - 1, 0);
            }
        }, 0);
    }, []);

    const onRichChange = useCallback((value: any, _delta: any, source: string, editor: ReactQuill.UnprivilegedEditor) => {
        // Only propagate user-initiated edits where the content actually changed.
        // - source === 'user' filters out Quill's mount-time normalization (source='api'),
        //   which fires when Quill parses the input HTML into its Delta representation.
        // - value !== lastValueRef.current is defensive: ensure we never mark the document
        //   dirty for a user-source event whose content matches what Quill last emitted.
        if (source === 'user' && value !== lastValueRef.current) {
            // Use the value parameter (getSemanticHTML) rather than editor.getHTML() (innerHTML).
            // react-quill-new 3.7.0 stores getSemanticHTML() internally; passing innerHTML back
            // as the value prop creates a mismatch that triggers clipboard.convert round-trips,
            // which strip trailing whitespace.
            props.onChange(value, editor.getText());
            hasChangedRef.current = true;
        }
        lastValueRef.current = value;
    }, [props]);

    /**
     * Render plain text
     *
     * @returns {*}
     */
    const renderPlain = useCallback(() => {
        return (
            <div className='text-area'>
                {props.isEditing
                 ? <textarea key="text" rows={props.isExpanded ? 100 : 20}
                             onChange={onChange}
                             onBlur={onBlur}
                             value={props.content || ''}
                 />
                 : <div key="text" className='text-area__content'>{props.content || ''}</div>
                }
            </div>
        );
    }, [props.isEditing, props.isExpanded, props.content, onChange, onBlur]);

    /**
     * Render rich text
     */
    const renderRich = useCallback(() => {
        return (
            <div className='text-area' ref={containerRef}>
                <ReactQuill ref={quillRef}
                            bounds='.text-area'
                            theme="snow"
                            onChange={onRichChange}
                            onBlur={onRichBlur}
                            onFocus={onRichFocus}
                            placeholder='Put your notes here'
                            preserveWhitespace={true}
                            value={props.content || ''}
                            readOnly={!props.isEditing}
                            formats={formats}
                            modules={modules}
                />
            </div>
        );
    }, [onRichChange, onRichBlur, onRichFocus, props.content, props.isEditing]);

    if (props.isRichText) return renderRich();
    else return renderPlain();
}

export type TextAreaProps = {
    content?: string,
    isEditing?: boolean,
    onChange?: (html: string, text: string) => void,
    onBlur?: (html: string, text: string) => void,
    isRichText?: boolean,
    isExpanded?: boolean
};

export default TextArea;

